// ============================================================
// PublicExamsPage.jsx
// صفحه‌ی کامل آزمون‌های عمومی — نسخه‌ی نهایی و یکپارچه
// ------------------------------------------------------------
// • اتصال کامل به AuthContext / CoinContext / ExamContext
// • داده‌ی per-user از exam.results
// • پشتیبانی Dark/Light Mode
// • محاسبه‌ی زنده‌ی لیگ، مدال‌ها، استریک، سطح، چالش روزانه
// • ۳ مودال: کارنامه، کمبود سکه، پیش‌نمایش
// ============================================================

import React, {
  useState, useEffect, useMemo, useCallback, useRef,
} from 'react';
import { useNavigate } from 'react-router-dom';
import moment from 'moment-jalaali';
import './PublicExamsPage.css';

import { useAuth } from '../../context/AuthContext.jsx';
import { useCoins } from '../../context/CoinContext.jsx';
import {
  useExams, toNumber, idsEqual, calculatePassThreshold,
} from './ExamContext.jsx';
import { getImageUrl } from '../../services/ImageService';
import {
  getGrade as getGradeMeta,
  getField as getFieldMeta,
  normalizeGradeId,
  normalizeFieldId,
  needsField,
} from '../../core/education/curriculum.js';

// ═══════════════════════════════════════════════════════════════
//  HELPERS
// ═══════════════════════════════════════════════════════════════
const FA_DIGITS = '۰۱۲۳۴۵۶۷۸۹';

const toFa = (v) => {
  if (v === undefined || v === null) return '';
  return String(v).replace(/[0-9]/g, (d) => FA_DIGITS[+d]);
};

const faNum = (n, decimals = 0) => {
  const num = Number(n);
  if (!Number.isFinite(num)) return '—';
  return toFa(num.toFixed(decimals));
};

const getUserDisplayName = (u) => {
  if (!u) return 'کاربر';
  if (u.firstName && u.lastName) return `${u.firstName} ${u.lastName}`.trim();
  if (u.firstName) return u.firstName;
  if (u.lastName) return u.lastName;
  if (u.name) return u.name;
  if (u.fullName) return u.fullName;
  if (u.username) return u.username;
  return 'کاربر';
};

const getSubjectIcon = (subject) => {
  const map = {
    'ریاضی': '🔬', 'فیزیک': '⚡', 'شیمی': '🧪', 'زیست': '🧬',
    'فارسی': '📜', 'عربی': '🌙', 'زبان': '🌍', 'دینی': '📖',
    'قرآن': '📿', 'علوم': '🔭', 'مطالعات': '🗺️', 'هدیه': '🎁',
  };
  return map[subject] || '📝';
};

// ─────────────────────────────────────────────────────────────
//  خواندن/نوشتن کلیدهای localStorage مخصوص این صفحه
// ─────────────────────────────────────────────────────────────
const loadSeenIds = (user) => {
  if (!user?.id) return [];
  try {
    // ✅ از خود کاربر در سرور می‌خونیم
    return Array.isArray(user.publicExamsSeenIds) ? user.publicExamsSeenIds : [];
  } catch { return []; }
};

const saveSeenIds = async (userId, ids) => {
  if (!userId) return;
  const trimmed = [...new Set(ids)].slice(-1000);
  try {
    const API = process.env.REACT_APP_API_URL || '';
    await fetch(`${API}/users/${userId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ publicExamsSeenIds: trimmed }),
    });
  } catch (e) {
    console.warn('⚠️ ذخیره‌ی seenIds در سرور ناموفق:', e);
  }
};

// ═══════════════════════════════════════════════════════════════
//  COMPUTE: Exam Card State
// ═══════════════════════════════════════════════════════════════
function computeExamCardState({ exam, userId, coins, now, seenIds = [], paidExams = [] }) {
  // ═══════════════════════════════════════════════════════════
  //  ۱. داده‌های پایه
  // ═══════════════════════════════════════════════════════════
  const userResult = exam.results?.find((r) => idsEqual(r.studentId, userId)) || null;
  const attemptsUsed = exam.results?.filter((r) => idsEqual(r.studentId, userId)).length || 0;
  const limit = Math.max(1, exam.limitAttempts || 1);
  const attemptsLeft = Math.max(0, limit - attemptsUsed);

  const hasResult = !!userResult;
  const isFinalSubmit = userResult?.isFinalSubmit === true;
  const needsCorrection = hasResult && userResult.isEssayGraded === false;
  const isAbsent = userResult?.isAbsent === true;

  const maxScore = (exam.questions || []).reduce((s, q) => s + toNumber(q.score, 1), 0);
  const passThreshold = calculatePassThreshold(maxScore);
  const userScore = hasResult ? toNumber(userResult.score, 0) : null;
  const hasPassed = hasResult && userScore >= passThreshold;

  // ═══════════════════════════════════════════════════════════
  //  ۲. محاسبات میانگین کل
  // ═══════════════════════════════════════════════════════════
  const allResults = (exam.results || []).filter((r) => r.isFinalSubmit);
  const avgAllScores = allResults.length
    ? allResults.reduce((s, r) => s + toNumber(r.score, 0), 0) / allResults.length
    : 0;
  const avgAllPercent = maxScore > 0 ? Math.round((avgAllScores / maxScore) * 100) : 0;

  const progressPercent = hasResult
    ? (maxScore > 0 ? Math.round((userScore / maxScore) * 100) : 0)
    : avgAllPercent;
  const progressValue = hasResult
    ? `${faNum(userScore, 1)} / ${faNum(maxScore)}`
    : `${faNum(avgAllScores, 1)} / ${faNum(maxScore)}`;
  const progressLabel = hasResult ? 'نمره شما' : 'میانگین بازیکنان';
  const progressColor = hasResult
    ? (hasPassed ? 'var(--emerald)' : 'var(--rose)')
    : 'var(--primary)';

  // ═══════════════════════════════════════════════════════════
  //  ۳. قفل‌ها
  // ═══════════════════════════════════════════════════════════
  const startTime = exam.startTime ? moment(exam.startTime) : null;
  const endTime = exam.endTime ? moment(exam.endTime) : null;
  const isTimeOpen =
    (!startTime || now.isAfter(startTime)) && (!endTime || now.isBefore(endTime));

  const requiredCoins = toNumber(exam.requiredCoins, 0);
  const hasEnoughCoins = coins >= requiredCoins;

  const isLockedByEmpty = !exam.questions || exam.questions.length === 0;
  const isLockedByStatus = ['pending', 'closed'].includes(exam.statusType);
  const isLockedByTime = !isTimeOpen && exam.statusType === 'active';
  const isLockedByAttempts = attemptsLeft === 0 && !needsCorrection && !isAbsent && !hasResult;
  const isLockedByCoins = requiredCoins > 0 && !hasEnoughCoins;

  const lockReason = isLockedByEmpty ? 'empty'
    : isLockedByStatus ? exam.statusType
    : isLockedByTime ? 'time'
    : isLockedByAttempts ? 'attempts'
    : isLockedByCoins ? 'coins'
    : null;

  const isLocked = !!lockReason && !isFinalSubmit && !needsCorrection;

  // ═══════════════════════════════════════════════════════════
  //  ۴. تعیین STATE (یکی از حالت‌های کاربر)
  // ═══════════════════════════════════════════════════════════
  let stateType = 'none';
  if (isAbsent) stateType = 'absent';
  else if (needsCorrection) stateType = 'correction';
  else if (isFinalSubmit && hasPassed) stateType = 'passed';
  else if (isFinalSubmit && !hasPassed) stateType = 'failed';
  else if (lockReason === 'empty') stateType = 'lock_empty';
  else if (lockReason === 'attempts') stateType = 'lock_attempts';
  else if (lockReason === 'time') stateType = 'lock_time';
  else if (lockReason === 'coins') stateType = 'lock_coins';
  else if (lockReason === 'pending') stateType = 'lock_pending';
  else if (lockReason === 'closed') stateType = 'lock_closed';

  // ═══════════════════════════════════════════════════════════
  //  ۵. ساخت badgeها — ۴ لایه
  // ═══════════════════════════════════════════════════════════
  const badges = [];

  // ── LAYER 1: State badge ──
  switch (stateType) {
    case 'absent':          badges.push({ kind: 'danger',  label: '❌ غایب' });              break;
    case 'correction':      badges.push({ kind: 'warning', label: '⏳ در انتظار تصحیح' });    break;
    case 'passed':          badges.push({ kind: 'success', label: '✅ قبول' });               break;
    case 'failed':          badges.push({ kind: 'danger',  label: '❌ مردود' });              break;
    case 'lock_empty':      badges.push({ kind: 'warning', label: '🏗️ آماده‌سازی' });         break;
    case 'lock_attempts':   badges.push({ kind: 'lock',    label: '⛔ تلاش تمام' });          break;
    case 'lock_time':       badges.push({ kind: 'warning', label: '⏳ خارج از زمان' });        break;
    case 'lock_coins':      badges.push({ kind: 'lock',    label: `🔒 ${faNum(requiredCoins)} سکه` }); break;
    case 'lock_pending':    badges.push({ kind: 'warning', label: '⏳ در انتظار انتشار' });    break;
    case 'lock_closed':     badges.push({ kind: 'lock',    label: '🔒 بسته شده' });           break;
  }

  // ── LAYER 2: Price badge ──
  // در stateهای خاص نمایش داده می‌شه (نه در قفل‌هایی که پول گزینه نیست)
  const showPrice = ['none', 'passed', 'failed', 'lock_attempts', 'lock_time'].includes(stateType);
  if (showPrice) {
    if (requiredCoins > 0) {
      badges.push({ kind: 'gold', label: `🥇 ${faNum(requiredCoins)} سکه` });
    } else {
      badges.push({ kind: 'free', label: '🆓 رایگان' });
    }
  }

  // ── LAYER 3: New badge ──
  const canBeNew = !seenIds.includes(exam.id) &&
                   !hasResult &&
                   !['lock_empty', 'lock_pending', 'lock_closed'].includes(stateType);

  if (canBeNew) {
    const newHours = toNumber(exam.newBadgeDuration, 24);
    const isAutoNew = newHours > 0 &&
      exam.createdAt &&
      moment(exam.createdAt).isAfter(now.clone().subtract(newHours, 'hours'));
    const isForceNew = exam.forceNew === true;
    if (isAutoNew || isForceNew) {
      badges.push({ kind: 'new', label: '✨ جدید' });
    }
  }

  // ── LAYER 4: Hot badge ──
  const canBeHot = !['absent', 'correction', 'lock_empty', 'lock_pending', 'lock_closed']
    .includes(stateType);

  if (canBeHot) {
    const hotMinParticipants = toNumber(exam.hotThresholdParticipants, 1000);
    const hotMinRecent = toNumber(exam.hotThresholdRecentResults, 100);
    const isAutoHot =
      (exam.participants || 0) >= hotMinParticipants ||
      (exam.results || []).filter((r) =>
        r.submittedAt && moment(r.submittedAt).isAfter(now.clone().subtract(7, 'days'))
      ).length >= hotMinRecent;
    const isForceHot = exam.forceHot === true;
    if (isAutoHot || isForceHot) {
      badges.push({ kind: 'hot', label: '🔥 داغ' });
    }
  }

  // ═══════════════════════════════════════════════════════════
  //  ۶. تعیین cardType (فقط برای رنگ کارت)
  // ═══════════════════════════════════════════════════════════
  let cardType = 'free';
  if (stateType === 'lock_empty') {
    cardType = 'locked';
  } else if (requiredCoins > 0) {
    cardType = 'elite';
  } else {
    const hasNew = badges.some(b => b.kind === 'new');
    const hasHot = badges.some(b => b.kind === 'hot');
    if (hasNew) cardType = 'new';
    else if (hasHot) cardType = 'hot';
  }

  // ═══════════════════════════════════════════════════════════
  //  ۷. متن دکمه
  // ═══════════════════════════════════════════════════════════
  let btnText = '🚀 شروع';
  let btnAction = 'start';
  let btnDisabled = false;

  if (isAbsent) { btnText = '🏆 کارنامه'; btnAction = 'result'; }
  else if (needsCorrection) { btnText = '⏳ در انتظار تصحیح'; btnAction = 'pending'; btnDisabled = true; }
  else if (isFinalSubmit) { btnText = '🏆 کارنامه'; btnAction = 'result'; }
  else if (lockReason === 'coins') {
    btnText = `🪙 خرید ${faNum(Math.max(0, requiredCoins - coins))} سکه`;
    btnAction = 'needCoins';
  }
  else if (lockReason === 'attempts') { btnText = '⛔ تلاش تمام'; btnAction = 'limit'; btnDisabled = true; }
  else if (lockReason === 'time') { btnText = '⏳ خارج از زمان'; btnAction = 'time'; btnDisabled = true; }
  else if (lockReason === 'pending') { btnText = '⏳ منتظر'; btnAction = 'pending'; btnDisabled = true; }
  else if (lockReason === 'closed') { btnText = '🔒 بسته'; btnAction = 'closed'; btnDisabled = true; }
  else if (lockReason === 'empty') { btnText = '🏗️ آماده‌سازی'; btnAction = 'empty'; btnDisabled = true; }

  // ═══════════════════════════════════════════════════════════
  //  ۸. عنوان پایه و رشته (با try/catch)
  // ═══════════════════════════════════════════════════════════
  let gradeTitle = exam.grade || '';
  try {
    const nGrade = normalizeGradeId(exam.grade);
    if (nGrade) gradeTitle = getGradeMeta(nGrade)?.title || exam.grade;
  } catch {}

  let fieldTitle = '';
  try {
    const nField = normalizeFieldId(exam.field);
    if (nField) fieldTitle = getFieldMeta(nField)?.title || '';
  } catch {}

  // ═══════════════════════════════════════════════════════════
  //  ۹. خروجی نهایی
  // ═══════════════════════════════════════════════════════════
  return {
    exam, userResult,
    hasResult, isFinalSubmit, needsCorrection, isAbsent,
    attemptsUsed, attemptsLeft, limit,
    userScore, hasPassed,
    maxScore, passThreshold, avgAllScores, avgAllPercent,
    progressPercent, progressValue, progressLabel, progressColor,
    isLocked, lockReason, canStart: !isLocked && !isFinalSubmit && !needsCorrection && !isAbsent,
    requiredCoins, hasEnoughCoins,
    stateType,
    cardType,
    badges,
    // سازگاری با کد قبلی (اگه جایی از این‌ها استفاده می‌شد)
    secondaryBadge: null,
    btnText, btnAction, btnDisabled,
    gradeTitle, fieldTitle,
  };
}

// ═══════════════════════════════════════════════════════════════
//  COMPUTE: Streak / Level / Today Stats
// ═══════════════════════════════════════════════════════════════
function computeStreak(userId, exams) {
  if (!userId) return 0;
  const days = new Set();
  exams.forEach((e) => {
    (e.results || []).forEach((r) => {
      if (!idsEqual(r.studentId, userId)) return;
      if (!r.isFinalSubmit || !r.submittedAt) return;
      days.add(moment(r.submittedAt).format('YYYY-MM-DD'));
    });
  });
  if (days.size === 0) return 0;

  let streak = 0;
  let cursor = moment();
  if (!days.has(cursor.format('YYYY-MM-DD'))) {
    cursor = cursor.subtract(1, 'day');
    if (!days.has(cursor.format('YYYY-MM-DD'))) return 0;
  }
  while (days.has(cursor.format('YYYY-MM-DD'))) {
    streak++;
    cursor = cursor.subtract(1, 'day');
  }
  return streak;
}

function computeLevel(xp) {
  const xpNum = Math.max(0, Math.floor(Number(xp) || 0));
  const perLevel = 500;
  return {
    level: Math.floor(xpNum / perLevel) + 1,
    xpInLevel: xpNum % perLevel,
    xpToNext: perLevel - (xpNum % perLevel),
  };
}

function computeTodayStats(userId, exams, coins) {
  const today = moment().format('YYYY-MM-DD');
  let completedToday = 0;
  let totalScore = 0, scoreCount = 0, totalMax = 0;

  exams.forEach((e) => {
    if (!e.isGlobal) return;
    (e.results || []).forEach((r) => {
      if (!idsEqual(r.studentId, userId) || !r.isFinalSubmit) return;
      const submitDate = r.submittedAt ? moment(r.submittedAt).format('YYYY-MM-DD') : null;
      if (submitDate === today) completedToday++;
      const max = (e.questions || []).reduce((s, q) => s + toNumber(q.score, 1), 0);
      if (max > 0) {
        totalScore += toNumber(r.score, 0);
        totalMax += max;
        scoreCount++;
      }
    });
  });

  return {
    completedToday,
    coins,
    avgScore: scoreCount > 0 ? (totalScore / scoreCount).toFixed(1) : '—',
    avgPercent: totalMax > 0 ? Math.round((totalScore / totalMax) * 100) : 0,
  };
}

// ═══════════════════════════════════════════════════════════════
//  COMPUTE: Achievements / Leaderboard / Daily Challenge
// ═══════════════════════════════════════════════════════════════
function computeAchievements(userId, exams, coins, streak) {
  let totalExams = 0, perfectScores = 0, totalScore = 0, totalMax = 0;
  exams.forEach((e) => {
    if (!e.isGlobal || e.isChallenge) return;
    const r = (e.results || []).find((x) => idsEqual(x.studentId, userId));
    if (!r || !r.isFinalSubmit) return;
    totalExams++;
    const max = (e.questions || []).reduce((s, q) => s + toNumber(q.score, 1), 0);
    if (max > 0) {
      totalScore += toNumber(r.score, 0);
      totalMax += max;
      if (toNumber(r.score) === max) perfectScores++;
    }
  });
  const avg20 = totalMax > 0 ? (totalScore / totalMax) * 20 : 0;

  const list = [
    { id: 'first', emoji: '🎯', label: 'شروع', unlocked: totalExams >= 1 },
    { id: 'ten', emoji: '🔥', label: '۱۰ آزمون', unlocked: totalExams >= 10 },
    { id: 'fifty', emoji: '⭐', label: '۵۰ آزمون', unlocked: totalExams >= 50 },
    { id: 'perfect', emoji: '💎', label: 'کامل', unlocked: perfectScores >= 1 },
    { id: 'avg18', emoji: '🏆', label: 'میانگین ۱۸', unlocked: avg20 >= 18 },
    { id: 'coins1k', emoji: '👑', label: 'هزار سکه', unlocked: coins >= 1000 },
    { id: 'streak7', emoji: '🔥', label: '۷ روز', unlocked: streak >= 7 },
    { id: 'streak30', emoji: '🌟', label: '۳۰ روز', unlocked: streak >= 30 },
  ];

  return {
    list,
    unlockedCount: list.filter((a) => a.unlocked).length,
    lockedCount: list.filter((a) => !a.unlocked).length,
  };
}

function getCurrentSeason(now = moment()) {
  const jMonth = now.jMonth() + 1;
  const year = now.jYear();
  let name, startMonth, endMonth;
  if (jMonth <= 3) { name = 'بهار'; startMonth = 1; endMonth = 3; }
  else if (jMonth <= 6) { name = 'تابستان'; startMonth = 4; endMonth = 6; }
  else if (jMonth <= 9) { name = 'پاییز'; startMonth = 7; endMonth = 9; }
  else { name = 'زمستان'; startMonth = 10; endMonth = 12; }
  const start = moment(`${year}/${startMonth}/1`, 'jYYYY/jMM/jDD').startOf('day');
  const end = moment(`${year}/${endMonth}/1`, 'jYYYY/jMM/jDD').endOf('month');
  return { name: `${name} ${toFa(year)}`, year, start, end };
}

function computeLeaderboard({ userId, exams, users, limit = 10 }) {
  const season = getCurrentSeason();
  const bucket = new Map();

  exams.forEach((e) => {
    if (!e.isGlobal || e.isChallenge) return;
    (e.results || []).forEach((r) => {
      if (!r.isFinalSubmit || !r.submittedAt) return;
      const at = moment(r.submittedAt);
      if (at.isBefore(season.start) || at.isAfter(season.end)) return;
      const max = (e.questions || []).reduce((s, q) => s + toNumber(q.score, 1), 0);
      if (max === 0) return;
      const cur = bucket.get(r.studentId) || { exams: 0, totalScore: 0, totalMax: 0 };
      cur.exams++;
      cur.totalScore += toNumber(r.score, 0);
      cur.totalMax += max;
      bucket.set(r.studentId, cur);
    });
  });

  const list = [...bucket.entries()].map(([uid, b]) => {
    const u = users.find((x) => idsEqual(x.id, uid)) || {};
    const percent = b.totalMax > 0 ? Math.round((b.totalScore / b.totalMax) * 100) : 0;
    return {
      userId: uid,
      name: getUserDisplayName(u) || 'کاربر ناشناس',
      avatar: u.avatar || '🦸',
      exams: b.exams,
      percent,
      score: percent,
    };
  });

  list.sort((a, b) => b.score - a.score || b.exams - a.exams);
  list.forEach((x, i) => (x.rank = i + 1));

  const meIndex = list.findIndex((x) => idsEqual(x.userId, userId));
  const me = meIndex >= 0 ? list[meIndex] : null;

  return {
    season,
    top: list.slice(0, limit),
    me,
    meInTop: meIndex >= 0 && meIndex < limit,
    totalPlayers: list.length,
  };
}

function computeDailyChallenge(exams, now = moment()) {
  const today = now.format('YYYY-MM-DD');
  const challenge = exams.find((e) => {
    if (!e.isGlobal || !e.isChallenge || !e.isDailyChallenge) return false;
    if (e.statusType !== 'active') return false;
    const start = e.startTime ? moment(e.startTime) : (e.date ? moment(e.date) : null);
    if (!start) return false;
    return start.format('YYYY-MM-DD') === today;
  });
  return challenge ? { exists: true, challenge } : { exists: false };
}

function rankExams(states, sortBy = 'smart') {
  const arr = [...states];
  switch (sortBy) {
    case 'newest':
      return arr.sort((a, b) => new Date(b.exam.createdAt || 0) - new Date(a.exam.createdAt || 0));
    case 'popular':
      return arr.sort((a, b) => (b.exam.participants || 0) - (a.exam.participants || 0));
    case 'easy':
      return arr.sort((a, b) => b.avgAllPercent - a.avgAllPercent);
    case 'hard':
      return arr.sort((a, b) => a.avgAllPercent - b.avgAllPercent);
    case 'smart':
    default:
      return arr.sort((a, b) => {
        const sA = (a.canStart ? 1000 : 0) + a.attemptsLeft * 100 - a.attemptsUsed * 50
          + (a.cardType === 'new' ? 80 : 0) + (a.cardType === 'hot' ? 40 : 0);
        const sB = (b.canStart ? 1000 : 0) + b.attemptsLeft * 100 - b.attemptsUsed * 50
          + (b.cardType === 'new' ? 80 : 0) + (b.cardType === 'hot' ? 40 : 0);
        return sB - sA;
      });
  }
}

function computeNewExamCount(states, seenIds) {
  return states.filter((s) => {
    if (!s.exam.createdAt) return false;
    const recent = moment(s.exam.createdAt).isAfter(moment().subtract(7, 'days'));
    const unseen = !seenIds.includes(s.exam.id);
    return recent && unseen;
  }).length;
}

// ═══════════════════════════════════════════════════════════════
//  MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════
const PublicExamsPage = () => {
  const navigate = useNavigate();
  const { user, refreshUser, updateUser } = useAuth();
  const { coins, xp, removeCoins, addRewards } = useCoins();
  const {
    exams,
    getGlobalExams,
    getGlobalCategories,
    updateExamResults,
  } = useExams();

  const userId = user?.id || null;
  const studentGradeId = useMemo(() => user?.grade ? normalizeGradeId(user.grade) || null : null, [user?.grade]);
  const studentFieldId = useMemo(() => user?.field ? normalizeFieldId(user.field) || null : null, [user?.field]);

  // ─── Theme ───
  const [isDark, setIsDark] = useState(() => {
    try { return localStorage.getItem('navbar2-drawer-dark') === '1'; }
    catch { return false; }
  });

  useEffect(() => {
    const handleStorage = (e) => {
      if (e.key === 'navbar2-drawer-dark') setIsDark(e.newValue === '1');
    };
    window.addEventListener('storage', handleStorage);
    const interval = setInterval(() => {
      try {
        const cur = localStorage.getItem('navbar2-drawer-dark') === '1';
        setIsDark((prev) => (prev !== cur ? cur : prev));
      } catch {}
    }, 500);
    return () => {
      window.removeEventListener('storage', handleStorage);
      clearInterval(interval);
    };
  }, []);

  // ─── State ───
  const [initialLoading, setInitialLoading] = useState(true);
  const [filters, setFilters] = useState({
    search: '',
    grade: 'auto',
    field: '',
    subject: 'all',
    book: 'all',
    chapter: 'all',
    type: 'all',
  });
  const [sortBy, setSortBy] = useState('smart');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [countdown, setCountdown] = useState('--:--:--');
  const [seenIds, setSeenIds] = useState(() => loadSeenIds(user));
  const [previewExam, setPreviewExam] = useState(null);
  const [resultExam, setResultExam] = useState(null);
  const [coinModal, setCoinModal] = useState(null);
  const [scrollVisible, setScrollVisible] = useState(false);
  const [now, setNow] = useState(moment());

  const gridRef = useRef(null);
  const dailyRef = useRef(null);

  // ─── Effects ───
  useEffect(() => {
    if (!user?.id) return;
    setSeenIds(loadSeenIds(user));
  }, [user?.id, user?.publicExamsSeenIds]);

  useEffect(() => {
    const t = setInterval(() => setNow(moment()), 30000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (exams.length >= 0 && userId) setInitialLoading(false);
  }, [exams, userId]);

  // Countdown
  useEffect(() => {
    const tick = () => {
      const diff = moment().endOf('day').diff(moment());
      if (diff <= 0) { setCountdown('--:--:--'); return; }
      const h = Math.floor(diff / 3600000);
      const m = Math.floor((diff % 3600000) / 60000);
      const s = Math.floor((diff % 60000) / 1000);
      const pad = (n) => String(n).padStart(2, '0');
      setCountdown(toFa(`${pad(h)}:${pad(m)}:${pad(s)}`));
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);

  // Scroll top
  useEffect(() => {
    const onScroll = () => setScrollVisible(window.scrollY > 400);
    window.addEventListener('scroll', onScroll);
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // ─── Source exams ───
  const rawExams = useMemo(() => {
    let list = getGlobalExams() || [];

    // پایه‌ی مؤثر: اگه کاربر فیلتر دستی زده، از اون استفاده کن؛ وگرنه پایه‌ی خودش
    const effectiveGradeId = filters.grade === 'auto'
      ? studentGradeId
      : (filters.grade || null);

    // رشته‌ی مؤثر (فقط برای پایه‌های ۱۰-۱۲)
    let effectiveFieldId = null;
    if (effectiveGradeId && needsField(effectiveGradeId)) {
      effectiveFieldId = filters.field ||
        (filters.grade === 'auto' ? studentFieldId : null);
    }

    if (effectiveGradeId) {
      list = list.filter((e) => {
        const g = normalizeGradeId(e.grade) || e.grade;
        return g === effectiveGradeId;
      });
    }

    if (effectiveFieldId) {
      list = list.filter((e) => !e.field || e.field === effectiveFieldId);
    }

    return list.filter((e) => e.statusType !== 'archived');
  }, [getGlobalExams, studentGradeId, studentFieldId, filters.grade, filters.field]);

  // ─── All Users (for leaderboard) ───
  const allUsers = useMemo(() => {
    try {
      if (typeof window !== 'undefined' && Array.isArray(window.__allUsersCache)) {
        return window.__allUsersCache;
      }
      return [];
    } catch { return []; }
  }, [exams]);

  // ─── Exam States ───
const examStates = useMemo(() => {
  return rawExams.map((exam) => computeExamCardState({
    exam, userId, coins, now, seenIds,
    paidExams: user?.paidExams || [],   // 🆕
  }));
}, [rawExams, userId, coins, now, seenIds, user?.paidExams]);

  // ─── Available filter options ───
  // 🆕 لیست پایه‌ها از exams مستقیم (نه rawExams) تا وقتی کاربر فیلتر پایه زده، لیست خالی نشه
  const availableGrades = useMemo(() => {
    const map = new Map();
    (exams || []).forEach((e) => {
      if (!e.isGlobal || e.isChallenge) return;
      if (e.statusType === 'archived') return;
      if (!e.grade) return;
      const id = normalizeGradeId(e.grade) || e.grade;
      if (!map.has(id)) map.set(id, getGradeMeta(id)?.title || e.grade);
    });
    return [...map.entries()].map(([id, title]) => ({ id, title }));
  }, [exams]);

  // 🆕 رشته‌های موجود برای پایه‌ی انتخاب‌شده (فقط ۱۰-۱۲)
  const availableFields = useMemo(() => {
    const effGrade = filters.grade === 'auto' ? studentGradeId : filters.grade;
    if (!effGrade || !needsField(effGrade)) return [];

    const set = new Set();
    (exams || []).forEach((e) => {
      if (!e.isGlobal || e.isChallenge) return;
      if (e.statusType === 'archived') return;
      const g = normalizeGradeId(e.grade) || e.grade;
      if (g !== effGrade) return;
      if (e.field) set.add(e.field);
    });
    return [...set].sort();
  }, [exams, filters.grade, studentGradeId]);

  const availableSubjects = useMemo(() => {
    const set = new Set(rawExams.map((e) => e.subject).filter(Boolean));
    return [...set].sort();
  }, [rawExams]);

  // 🆕 کتاب‌های موجود — بر اساس درس انتخاب‌شده
  const availableBooks = useMemo(() => {
    const base = filters.subject === 'all'
      ? rawExams
      : rawExams.filter((e) => e.subject === filters.subject);
    const set = new Set(base.map((e) => e.book).filter(Boolean));
    return [...set].sort();
  }, [rawExams, filters.subject]);

  // 🆕 فصل‌های موجود — بر اساس درس + کتاب انتخاب‌شده
  const availableChapters = useMemo(() => {
    const base = rawExams.filter((e) => {
      if (filters.subject !== 'all' && e.subject !== filters.subject) return false;
      if (filters.book !== 'all' && e.book !== filters.book) return false;
      return true;
    });
    const set = new Set(base.map((e) => e.chapter).filter(Boolean));
    return [...set].sort((a, b) => {
      const numA = parseInt(a.replace(/\D/g, ''), 10) || 0;
      const numB = parseInt(b.replace(/\D/g, ''), 10) || 0;
      return numA - numB || a.localeCompare(b);
    });
  }, [rawExams, filters.subject, filters.book]);

  // ─── Filtering ───
  const filteredStates = useMemo(() => {
    let list = examStates;

    // پایه
    if (filters.grade !== 'auto' && filters.grade !== 'all') {
      list = list.filter((s) => {
        const g = normalizeGradeId(s.exam.grade) || s.exam.grade;
        return g === filters.grade;
      });
    }

    // رشته (فقط اگه انتخاب شده)
    if (filters.field) {
      list = list.filter((s) => !s.exam.field || s.exam.field === filters.field);
    }

    // درس
    if (filters.subject !== 'all') {
      list = list.filter((s) => s.exam.subject === filters.subject);
    }

    // کتاب
    if (filters.book !== 'all') {
      list = list.filter((s) => s.exam.book === filters.book);
    }

    // فصل
    if (filters.chapter !== 'all') {
      list = list.filter((s) => s.exam.chapter === filters.chapter);
    }

    // جستجو
    if (filters.search.trim()) {
      const q = filters.search.trim().toLowerCase();
      list = list.filter((s) => {
        const hay = `${s.exam.title || ''} ${s.exam.book || ''} ${s.exam.subject || ''} ${s.exam.chapter || ''}`.toLowerCase();
        return hay.includes(q);
      });
    }

    // نوع
    switch (filters.type) {
      case 'free': list = list.filter((s) => ['free', 'new', 'hot'].includes(s.cardType)); break;
      case 'elite': list = list.filter((s) => s.cardType === 'elite'); break;
      case 'new': list = list.filter((s) => s.cardType === 'new'); break;
      case 'hot': list = list.filter((s) => s.cardType === 'hot'); break;
      case 'completed': list = list.filter((s) => s.isFinalSubmit); break;
      case 'pending': list = list.filter((s) => s.needsCorrection); break;
    }

    return list;
  }, [examStates, filters]);

  const sortedStates = useMemo(() => rankExams(filteredStates, sortBy), [filteredStates, sortBy]);

  // ─── Computed stats ───
  const todayStats = useMemo(
    () => computeTodayStats(userId, rawExams, coins),
    [userId, rawExams, coins]
  );

  const streak = useMemo(() => computeStreak(userId, exams || []), [userId, exams]);
  const levelInfo = useMemo(() => computeLevel(xp), [xp]);
  const achievements = useMemo(
    () => computeAchievements(userId, rawExams, coins, streak),
    [userId, rawExams, coins, streak]
  );
  const leaderboard = useMemo(
    () => computeLeaderboard({ userId, exams: rawExams, users: allUsers, limit: 6 }),
    [userId, rawExams, allUsers]
  );
















  useEffect(() => {
  const allChallenges = (exams || []).filter(e => e.isChallenge === true);
  console.log('🔍 تعداد کل چالش‌ها:', allChallenges.length);
  allChallenges.forEach(e => {
    console.log('  ▸', {
      title: e.title,
      isGlobal: e.isGlobal,
      isChallenge: e.isChallenge,
      isDailyChallenge: e.isDailyChallenge,
      isDailyChallenge_type: typeof e.isDailyChallenge,
      statusType: e.statusType,
      grade: e.grade,
      field: e.field,
      date: e.date,
      startTime: e.startTime,
      _gregorianDate: e._gregorianDate,
    });
  });
  console.log('🎓 studentGradeId:', studentGradeId);
  console.log('🎯 studentFieldId:', studentFieldId);
  console.log('📅 امروز (YYYY-MM-DD):', now.format('YYYY-MM-DD'));
  console.log('📅 امروز (jalali):', now.format('jYYYY-jMM-jDD'));
}, [exams, studentGradeId, studentFieldId, now]);








// چالش روزانه از exams مستقیم (چون getGlobalExams چالش‌ها رو حذف می‌کنه)
// چالش روزانه از exams مستقیم (چون getGlobalExams چالش‌ها رو حذف می‌کنه)
// 🆕 چالش روزانه فقط و فقط در تاریخ خودش نمایش داده میشه.
// گذشته: بسته و پنهان | آینده: پنهان | امروز: نمایش
const dailyChallengeInfo = useMemo(() => {
  if (!exams || exams.length === 0) {
    return { exists: false };
  }

  // ۱. همه‌ی چالش‌های روزانه (بدون isGlobal — چون چالش ذاتاً برای همه است)
  let candidates = exams.filter((e) => {
    if (!e || typeof e !== 'object') return false;
    if (e.isStepQuiz === true) return false;
    if (e.statusType !== 'active') return false;
    if (e.isDailyChallenge !== true) return false;
    return true;
  });

  // ۲. فیلتر پایه‌ی دانش‌آموز
  if (studentGradeId) {
    candidates = candidates.filter((e) => {
      const g = normalizeGradeId(e.grade) || e.grade;
      return g === studentGradeId;
    });
  }

  // ۳. فیلتر رشته (فقط پایه‌های ۱۰-۱۲)
  if (studentFieldId) {
    candidates = candidates.filter((e) => !e.field || e.field === studentFieldId);
  }

  // ۴. 🆕 فقط چالش امروز — نه گذشته، نه آینده
  // منطق: چالش روزانه تاریخ مشخصی داره. در همون تاریخ نمایش داده میشه،
  // بعدش خودکار بسته و پنهان میشه، و روز بعد چالش جدید (اگه باشه) جاش رو می‌گیره.
  const todayStr = now.format('YYYY-MM-DD');
  const challenge = candidates.find((e) => {
    // استخراج تاریخ از هر منبع ممکن (date → startTime → _gregorianDate)
    let examDate = '';
    if (e.date) {
      examDate = String(e.date).split('T')[0];
    } else if (e.startTime) {
      examDate = String(e.startTime).split('T')[0];
    } else if (e._gregorianDate) {
      examDate = String(e._gregorianDate).split('T')[0];
    }
    if (!examDate) return false;
    // ✅ فقط تاریخ دقیقاً برابر امروز
    return examDate === todayStr;
  });

  return challenge ? { exists: true, challenge } : { exists: false };
}, [exams, studentGradeId, studentFieldId, now]);

const dailyState = useMemo(() => {
  if (!dailyChallengeInfo.exists) return null;
  return computeExamCardState({
    exam: dailyChallengeInfo.challenge, userId, coins, now, seenIds,
    paidExams: user?.paidExams || [],   // 🆕
  });
}, [dailyChallengeInfo, userId, coins, now, seenIds, user?.paidExams]);

  const newExamCount = useMemo(
    () => computeNewExamCount(examStates, seenIds),
    [examStates, seenIds]
  );

  // ─── Mark seen ───
  useEffect(() => {
    if (!userId || initialLoading) return;
    const newIds = sortedStates.map((s) => s.exam.id).filter((id) => !seenIds.includes(id));
    if (newIds.length > 0) {
      const next = [...seenIds, ...newIds];
      setSeenIds(next);
      saveSeenIds(userId, next);   // async ولی منتظر نمی‌مونیم
    }
  }, [sortedStates, userId, initialLoading, seenIds]);

  // ═══════════════════════════════════════════════════════════════
  //  HANDLERS
  // ═══════════════════════════════════════════════════════════════
  const handleStartExam = useCallback((state) => {
    const { exam, requiredCoins, hasEnoughCoins, btnAction } = state;

    if (!userId) {
      if (window.confirm('برای شرکت در آزمون باید وارد حساب خود شوید. آیا می‌روید؟')) {
        navigate('/login');
      }
      return;
    }

    if (btnAction === 'needCoins') {
      setCoinModal({
        exam,
        required: requiredCoins,
        current: coins,
      });
      return;
    }

    if (btnAction === 'limit') {
      window.alert('⛔ شما تمام تلاش‌های مجاز این آزمون را استفاده کرده‌اید.');
      return;
    }

    if (btnAction === 'time') {
      window.alert('⏳ زمان شرکت در این آزمون به پایان رسیده است.');
      return;
    }

    if (btnAction === 'pending' || btnAction === 'closed' || btnAction === 'empty') {
      return;
    }

    // Paid exam
    if (requiredCoins > 0) {
      const paidExams = user?.paidExams || [];
      const alreadyPaid = paidExams.includes(exam.id);

      if (!alreadyPaid) {
        if (!hasEnoughCoins) {
          setCoinModal({ exam, required: requiredCoins, current: coins });
          return;
        }
        const ok = window.confirm(
          `این آزمون ${toFa(requiredCoins)} سکه هزینه دارد.\nموجودی: ${toFa(coins)} سکه\n\nآیا موافقت می‌کنید؟`
        );
        if (!ok) return;

        const removed = removeCoins(requiredCoins);
        if (removed <= 0) {
          window.alert('⚠️ خطا در کسر سکه. لطفاً دوباره تلاش کنید.');
          return;
        }

        // ✅ ثبت پرداخت از طریق AuthContext
        // updateUser خودش هم state لوکال رو آپدیت می‌کنه و هم به سرور PATCH می‌زنه
        updateUser({ paidExams: [...paidExams, exam.id] });
      }
    }

    navigate(`/exams/take/${exam.id}`);
  }, [userId, coins, removeCoins, navigate, user?.paidExams, updateUser]);

  const handleViewResult = useCallback((state) => {
    setResultExam(state);
  }, []);

  const handleCardClick = useCallback((e, state) => {
    if (e.target.closest('.exam-start-btn')) return;
    if (e.target.closest('.exam-price')) return;
    if (state.isLocked) return;
    setPreviewExam(state);
  }, []);

  const handleFilterChange = useCallback((key, value) => {
    setFilters((prev) => {
      const next = { ...prev, [key]: value };

      // تغییر پایه → ریست رشته، درس، کتاب، فصل
      if (key === 'grade') {
        next.field = '';
        next.subject = 'all';
        next.book = 'all';
        next.chapter = 'all';
      }
      // تغییر رشته → ریست درس، کتاب، فصل
      if (key === 'field') {
        next.subject = 'all';
        next.book = 'all';
        next.chapter = 'all';
      }
      // تغییر درس → ریست کتاب، فصل
      if (key === 'subject') {
        next.book = 'all';
        next.chapter = 'all';
      }
      // تغییر کتاب → ریست فصل
      if (key === 'book') {
        next.chapter = 'all';
      }

      return next;
    });
  }, []);

  const handleClearFilters = useCallback(() => {
    setFilters({
      search: '',
      grade: 'auto',
      field: '',
      subject: 'all',
      book: 'all',
      chapter: 'all',
      type: 'all',
    });
  }, []);

  const scrollToDaily = useCallback(() => {
    dailyRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, []);

  const scrollToGrid = useCallback(() => {
    gridRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  const goToCoinStore = useCallback(() => {
    setCoinModal(null);
    navigate('/coins');
  }, [navigate]);

  // ═══════════════════════════════════════════════════════════════
  //  RENDER HELPERS
  // ═══════════════════════════════════════════════════════════════
  const renderTopBar = () => (
    <header className="topbar">
      <button
        className="back-btn"
        onClick={() => navigate(-1)}
        aria-label="بازگشت"
      >
        ←
      </button>
      <div className="topbar-title">
        <span className="pulse-dot" />
        <span className="title-text">سالن آزمون‌های عمومی</span>
      </div>
      <div className="user-stats">
        <div className="stat-chip level" title="سطح شما">
          <span className="ico">⭐</span>
          <span>سطح {faNum(levelInfo.level)}</span>
        </div>
        {/*<div className="stat-chip xp" title="امتیاز تجربه">
          <span className="ico">✨</span>
          <span>{faNum(xp)}</span>
        </div>*/}
        <div className="stat-chip coins" title="سکه‌های شما">
          <span className="ico">🪙</span>
          <span>{faNum(coins)}</span>
        </div>
        {/*{streak > 0 && (
          <div className="stat-chip streak" title="روزهای پیوسته">
            <span className="ico">🔥</span>
            <span>{faNum(streak)} روز</span>
          </div>
        )}*/}
      </div>
      {/*<button
        className="avatar-btn"
        onClick={() => navigate('/avatars')}
        title="پروفایل"
        aria-label="پروفایل"
      >
        {user?.avatar && user.avatar.startsWith('/') ? (
          <img
            src={getImageUrl(user.avatar)}
            alt="آواتار"
            style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }}
          />
        ) : (
          <span>{user?.avatar || '🦸'}</span>
        )}
      </button>*/}
    </header>
  );

  const renderHero = () => (
    <section className="hero">
      <div className="hero-inner">
        <div className="hero-content">
          {newExamCount > 0 && (
            <div className="hero-badge">
              <span>{toFa(newExamCount)} آزمون جدید امشب اضافه شد</span>
            </div>
          )}
          <h1 className="hero-title">
            <span className="gradient-text">به میدان رقابت خوش آمدی</span>
          </h1>
          <p className="hero-desc">
            اینجا آزمون‌های عمومی برای همه‌ی پایه‌ها و کتاب‌ها آزاد است.
            درس بخوان، آزمون بده، سکه و امتیاز بگیر، و در لیگ فصلی بالا برو.
          </p>
          <div className="hero-actions">
            <button className="btn btn-primary" onClick={scrollToDaily}>
              ⚡ چالش امروز
            </button>
            <button className="btn btn-outline" onClick={scrollToGrid}>
              📚 مرور آزمون‌ها
            </button>
          </div>
        </div>
        <div className="hero-visual">
          <div className="trophy-orbit">
            <div className="orbit-ring"><div className="orbit-dot">📝</div></div>
            <div className="orbit-ring"><div className="orbit-dot">🎯</div></div>
            <div className="orbit-ring"><div className="orbit-dot">🏆</div></div>
            <div className="trophy-core">🏆</div>
          </div>
        </div>
      </div>
    </section>
  );

  const renderTodayStats = () => (
    <div className="today-row">
      <div className="today-card c-1">
        <div className="today-icon">✅</div>
        <div className="today-info">
          <div className="today-value">{toFa(todayStats.completedToday)}</div>
          <div className="today-label">آزمون کامل‌شده امروز</div>
        </div>
      </div>
      <div className="today-card c-2">
        <div className="today-icon">🪙</div>
        <div className="today-info">
          <div className="today-value">{faNum(todayStats.coins)}</div>
          <div className="today-label">سکه در کیف</div>
        </div>
      </div>
      <div className="today-card c-3">
        <div className="today-icon">📊</div>
        <div className="today-info">
          <div className="today-value">{todayStats.avgScore === '—' ? '—' : faNum(todayStats.avgScore, 1)}</div>
          <div className="today-label">میانگین نمرات</div>
        </div>
      </div>
      <div className="today-card c-4">
        <div className="today-icon">🔥</div>
        <div className="today-info">
          <div className="today-value">{toFa(streak)}</div>
          <div className="today-label">استریک روزانه</div>
        </div>
      </div>
    </div>
  );

  const renderDailyChallenge = () => {
    if (!dailyChallengeInfo.exists || !dailyState) {
      return (
        <section ref={dailyRef} className="daily-challenge" style={{ background: 'linear-gradient(135deg, rgba(100, 116, 139, 0.08), rgba(71, 85, 105, 0.05))', borderColor: 'rgba(148, 163, 184, 0.25)' }}>
          <div className="daily-inner">
            <div className="daily-icon-wrap">
              <div className="daily-icon" style={{ background: 'linear-gradient(135deg, #64748b, #475569)' }}>🕐</div>
            </div>
            <div className="daily-content">
              <h3 className="daily-title">چالش امروز <span style={{ color: 'var(--text-muted)' }}>به‌زودی</span></h3>
              <p className="daily-desc">امروز چالشی وجود ندارد. فردا صبح یک چالش تازه منتظر شماست!</p>
            </div>
          </div>
        </section>
      );
    }

    const ch = dailyState.exam;
    const isCompleted = dailyState.isFinalSubmit;
    const needsCorrection = dailyState.needsCorrection;

    return (
      <section ref={dailyRef} className="daily-challenge">
        <div className="daily-inner">
          <div className="daily-icon-wrap">
            <div className="daily-icon">⚡</div>
            <div className="daily-icon-badge">فوری</div>
          </div>
          <div className="daily-content">
            <h3 className="daily-title">
              چالش امروز: <span>{ch.title}</span>
            </h3>
            <p className="daily-desc">
              {needsCorrection
                ? '⏳ پاسخ‌های شما در حال تصحیح است.'
                : isCompleted
                ? '✅ شما امروز این چالش را انجام دادید!'
                : `${faNum(ch.questions?.length || 0)} سوال، ${faNum(ch.duration || 0)} دقیقه. با نمره‌ی بالای ۸۰٪ جایزه بگیر!`}
            </p>
            <div className="daily-meta">
              <span className="daily-meta-chip">🎯 {faNum(ch.questions?.length || 0)} سوال</span>
              <span className="daily-meta-chip">⏱ {faNum(ch.duration || 0)} دقیقه</span>
              {ch.requiredCoins > 0 && (
                <span className="daily-meta-chip">🪙 {faNum(ch.requiredCoins)} سکه</span>
              )}
            </div>
          </div>
          <div className="daily-timer">
            <div className="daily-timer-value" style={{ color: dailyState.isFinalSubmit ? 'var(--emerald)' : 'var(--gold)' }}>
              {countdown}
            </div>
            <div className="daily-timer-label">تا پایان چالش</div>
          </div>
        </div>
        <div style={{ marginTop: 20, position: 'relative', zIndex: 1 }}>
          {needsCorrection ? (
            <button className="btn btn-gold" disabled style={{ opacity: 0.7 }}>
              ⏳ در انتظار تصحیح
            </button>
          ) : isCompleted ? (
            <button
              className="btn btn-gold"
              style={{ padding: '14px 34px', fontSize: 15 }}
              onClick={() => handleViewResult(dailyState)}
            >
              🏆 مشاهده کارنامه
            </button>
          ) : (
            <button
              className="btn btn-gold"
              style={{ padding: '14px 34px', fontSize: 15 }}
              onClick={() => handleStartExam(dailyState)}
            >
              🚀 شروع چالش امروز
            </button>
          )}
        </div>
      </section>
    );
  };

  const renderFilters = () => (

    <div className="filters-block">
              <div className="sec-head-left">
          <div className="sec-tag" style={{
            '--tag-bg': 'rgba(34, 211, 238, 0.1)',
            '--tag-border': 'rgba(34, 211, 238, 0.3)',
            '--tag-color': 'var(--accent)',
          }}>
            ◆ آزمون‌های در دسترس
          </div>
          <h2 className="sec-title">همه آزمون‌های عمومی</h2>
          {/*<p className="sec-sub">
            {faNum(sortedStates.length)} آزمون مطابق فیلتر شما
          </p>*/}
        </div>
      

      {/* Grade row — فقط پایه‌ی خود دانش‌آموز */}
      <div className="filter-chips">
        <span className="chip-label">🎓 پایه:</span>
        <button
          className={`chip ${filters.grade === 'auto' ? 'gold active' : ''}`}
          onClick={() => handleFilterChange('grade', 'auto')}
        >
          {studentGradeId
            ? `پایه من (${getGradeMeta(studentGradeId)?.title || studentGradeId})`
            : 'همه پایه‌ها'}
        </button>
      </div>

      {/* 🆕 رشته — فقط اگه پایه‌ی انتخاب‌شده ۱۰-۱۲ باشه و رشته‌ای موجود باشه */}
      {availableFields.length > 0 && (
        <div className="filter-chips" style={{ borderTop: 'none', paddingTop: 6 }}>
          <span className="chip-label">🎯 رشته:</span>
          <button
            className={`chip ${!filters.field ? 'active' : ''}`}
            onClick={() => handleFilterChange('field', '')}
          >
            همه
          </button>
          {availableFields.map((f) => (
            <button
              key={f}
              className={`chip ${filters.field === f ? 'active' : ''}`}
              onClick={() => handleFilterChange('field', f)}
            >
              {getFieldMeta(f)?.title || f}
            </button>
          ))}
        </div>
      )}

      {/* Subject row */}
      <div className="filter-chips" style={{ borderTop: 'none', paddingTop: 6 }}>
        <span className="chip-label">📚 درس:</span>
        <button
          className={`chip ${filters.subject === 'all' ? 'active' : ''}`}
          onClick={() => handleFilterChange('subject', 'all')}
        >
          همه
        </button>
        {availableSubjects.map((subj) => (
          <button
            key={subj}
            className={`chip ${filters.subject === subj ? 'active' : ''}`}
            onClick={() => handleFilterChange('subject', subj)}
          >
            {getSubjectIcon(subj)} {subj}
          </button>
        ))}
      </div>

      {/* 🆕 Book row — فقط اگه درس انتخاب شده باشه */}
      {filters.subject !== 'all' && availableBooks.length > 0 && (
        <div className="filter-chips" style={{ borderTop: 'none', paddingTop: 6 }}>
          <span className="chip-label">📖 کتاب:</span>
          <button
            className={`chip ${filters.book === 'all' ? 'active' : ''}`}
            onClick={() => handleFilterChange('book', 'all')}
          >
            همه
          </button>
          {availableBooks.map((book) => (
            <button
              key={book}
              className={`chip ${filters.book === book ? 'active' : ''}`}
              onClick={() => handleFilterChange('book', book)}
            >
              {book}
            </button>
          ))}
        </div>
      )}

      {/* 🆕 Chapter row — فقط اگه کتاب انتخاب شده باشه */}
      {filters.book !== 'all' && availableChapters.length > 0 && (
        <div className="filter-chips" style={{ borderTop: 'none', paddingTop: 6 }}>
          <span className="chip-label">📑 فصل:</span>
          <button
            className={`chip ${filters.chapter === 'all' ? 'active' : ''}`}
            onClick={() => handleFilterChange('chapter', 'all')}
          >
            همه
          </button>
          {availableChapters.map((chapter) => (
            <button
              key={chapter}
              className={`chip ${filters.chapter === chapter ? 'active' : ''}`}
              onClick={() => handleFilterChange('chapter', chapter)}
            >
              {chapter}
            </button>
          ))}
        </div>
      )}

      {/* Type row */}
      <div className="filter-chips" style={{ borderTop: 'none', paddingTop: 6 }}>
        <span className="chip-label">🎯 نوع:</span>
        <button
          className={`chip ${filters.type === 'all' ? 'active' : ''}`}
          onClick={() => handleFilterChange('type', 'all')}
        >
          همه
        </button>
        <button
          className={`chip ${filters.type === 'free' ? 'active' : ''}`}
          onClick={() => handleFilterChange('type', 'free')}
        >
          🆓 رایگان
        </button>
        <button
          className={`chip ${filters.type === 'elite' ? 'gold active' : ''}`}
          onClick={() => handleFilterChange('type', 'elite')}
        >
          🥇 طلایی
        </button>
        <button
          className={`chip ${filters.type === 'new' ? 'active' : ''}`}
          onClick={() => handleFilterChange('type', 'new')}
        >
          ✨ جدید
        </button>
        <button
          className={`chip ${filters.type === 'hot' ? 'active' : ''}`}
          onClick={() => handleFilterChange('type', 'hot')}
        >
          🔥 داغ
        </button>
        <button
          className={`chip ${filters.type === 'completed' ? 'active' : ''}`}
          onClick={() => handleFilterChange('type', 'completed')}
        >
          ✅ انجام‌شده
        </button>
        {/*<button
          className={`chip ${filters.type === 'pending' ? 'active' : ''}`}
          onClick={() => handleFilterChange('type', 'pending')}
        >
          ⏳ در انتظار
        </button>*/}
      </div>
      <div className="filter-row">
        <div className="filter-search">
          <span className="ico">🔍</span>
          <input
            type="text"
            value={filters.search}
            placeholder="جستجو در آزمون‌ها... (کتاب، فصل، عنوان)"
            onChange={(e) => handleFilterChange('search', e.target.value)}
          />
          {filters.search && (
            <button className="filter-clear" onClick={() => handleFilterChange('search', '')} aria-label="پاک کردن">
              ✕
            </button>
          )}
        </div>
      </div>
    </div>
  );

  const renderExamCard = (state, idx) => {
    const {
      exam, cardType, badges, secondaryBadge, btnText, btnAction, btnDisabled,
      progressPercent, progressValue, progressLabel, progressColor,
      isLocked, gradeTitle, fieldTitle,
    } = state;

    const icon = getSubjectIcon(exam.subject);
    const cardClass = `exam-card ${cardType === 'locked' ? 'locked' : cardType}`;

    return (
      <article
        key={exam.id}
        className={cardClass}
        style={{ animationDelay: `${Math.min(idx * 0.04, 0.6)}s` }}
        onClick={(e) => handleCardClick(e, state)}
      >
        <div className="exam-header">
          <div className="exam-icon-wrap">{icon}</div>
<div className="exam-badges">
  {badges.map((b, i) => (
    <span key={i} className={`badge badge-${b.kind}`}>
      {b.label}
    </span>
  ))}
</div>
        </div>

        <h3 className="exam-title">{exam.title}</h3>

        <div className="exam-tags">
          {gradeTitle && <span className="tag" data-icon="🎓">{gradeTitle}</span>}
          {fieldTitle && <span className="tag" data-icon="🎯">{fieldTitle}</span>}
          {exam.subject && <span className="tag" data-icon="📖">{exam.subject}</span>}
          {exam.chapter && <span className="tag" data-icon="📑">{exam.chapter}</span>}
        </div>

        <div className="exam-stats">
          <span className="exam-stat">📝 <span className="val">{faNum(exam.questions?.length || 0)}</span> سوال</span>
          <span className="exam-stat">⏱ <span className="val">{faNum(exam.duration || 0)}</span> دقیقه</span>
          <span className="exam-stat">👥 <span className="val">{faNum(exam.participants || 0)}</span> نفر</span>
        </div>

        <div className="exam-progress">
          <div className="exam-progress-label">
            <span>{progressLabel}</span>
            <span style={{ color: progressColor }}>{progressValue}</span>
          </div>
          <div className="exam-progress-bar">
            <div
              className="exam-progress-fill"
              style={{ width: `${Math.max(2, progressPercent)}%`, background: progressColor }}
            />
          </div>
        </div>

        <div className="exam-footer">
          <span className={`exam-price ${state.requiredCoins > 0 ? 'coin' : 'free'}`}>
            {state.requiredCoins > 0 ? `🪙 ${faNum(state.requiredCoins)} سکه` : '🆓 رایگان'}
          </span>
          <button
            className="exam-start-btn"
            disabled={btnDisabled}
            onClick={(e) => {
              e.stopPropagation();
              if (btnAction === 'start' || btnAction === 'needCoins') {
                handleStartExam(state);
              } else if (btnAction === 'result') {
                handleViewResult(state);
              }
            }}
          >
            {btnText}
          </button>
        </div>
      </article>
    );
  };

  const renderExamGrid = () => (
    <section ref={gridRef}>
      <div className="sec-head">
        {/*<div className="sec-head-left">
          <div className="sec-tag" style={{
            '--tag-bg': 'rgba(34, 211, 238, 0.1)',
            '--tag-border': 'rgba(34, 211, 238, 0.3)',
            '--tag-color': 'var(--accent)',
          }}>
            ◆ آزمون‌های در دسترس
          </div>
          <h2 className="sec-title">همه آزمون‌های عمومی</h2>
          <p className="sec-sub">
            {faNum(sortedStates.length)} آزمون مطابق فیلتر شما
          </p>
        </div>*/}
        {/*<div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            style={{
              padding: '8px 16px',
              borderRadius: 100,
              background: 'rgba(30, 38, 58, 0.7)',
              border: '1.5px solid var(--border)',
              color: 'var(--text-soft)',
              fontFamily: 'inherit',
              fontSize: 13,
              fontWeight: 700,
              cursor: 'pointer',
              outline: 'none',
            }}
          >
            <option value="smart">🎯 هوشمند</option>
            <option value="newest">✨ جدیدترین</option>
            <option value="popular">👥 محبوب‌ترین</option>
            <option value="easy">📈 آسان‌ترین</option>
            <option value="hard">🔥 سخت‌ترین</option>
          </select>
          {(filters.search ||
            filters.grade !== 'auto' ||
            filters.field ||
            filters.subject !== 'all' ||
            filters.book !== 'all' ||
            filters.chapter !== 'all' ||
            filters.type !== 'all') && (
            <button className="sec-link" onClick={handleClearFilters}>
              ✕ پاک کردن فیلترها
            </button>
          )}
        </div>*/}
      </div>

      <div className="exam-grid">
        {initialLoading ? (
          Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="exam-card skeleton" style={{ height: 340 }} />
          ))
        ) : sortedStates.length > 0 ? (
          sortedStates.map((s, idx) => renderExamCard(s, idx))
        ) : examStates.length === 0 ? (
          <div className="empty-state">
            <span className="empty-ico">📭</span>
            <div className="empty-title">هیچ آزمون عمومی موجود نیست</div>
            <div className="empty-desc">به‌زودی آزمون‌های جدید اضافه می‌شوند. منتظر باشید!</div>
          </div>
        ) : (
          <div className="empty-state">
            <span className="empty-ico">🔍</span>
            <div className="empty-title">هیچ آزمونی با فیلترهای فعلی پیدا نشد</div>
            <div className="empty-desc">فیلترها را تغییر دهید یا جستجو را پاک کنید</div>
          </div>
        )}
      </div>
    </section>
  );

  const renderLeague = () => (
    <div className="panel">
      <h3 className="panel-title">
        <span className="ico" style={{ '--icon-color': 'var(--gold)' }}>🏆</span>
        لیگ فصلی — {leaderboard.season.name}
      </h3>

      {leaderboard.top.length === 0 ? (
        <div style={{ padding: '30px 10px', textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
          هنوز کسی در این فصل شرکت نکرده. اولین نفر باش! 🚀
        </div>
      ) : (
        <>
          {leaderboard.top.map((row) => {
            const isMe = idsEqual(row.userId, userId);
            const rankClass = row.rank === 1 ? 'gold' : row.rank === 2 ? 'silver' : row.rank === 3 ? 'bronze' : '';
            const rankLabel = row.rank === 1 ? '🥇' : row.rank === 2 ? '🥈' : row.rank === 3 ? '🥉' : toFa(row.rank);
            const avColor = row.rank === 1 ? 'var(--gold)' : row.rank === 2 ? '#cbd5e1' : row.rank === 3 ? '#d97706' : 'var(--primary)';
            return (
              <div key={row.userId} className={`leader-row ${isMe ? 'me' : ''}`}>
                <span className={`leader-rank ${rankClass}`}>{rankLabel}</span>
                <span className="leader-avatar" style={{ '--av-color': avColor }}>
                  {row.avatar?.startsWith('/') ? (
                    <img src={getImageUrl(row.avatar)} alt="" style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} />
                  ) : (
                    row.avatar || '🦸'
                  )}
                </span>
                <span className="leader-name">{isMe ? 'شما' : row.name}</span>
                <span className="leader-score">🎯 {faNum(row.score)}٪</span>
              </div>
            );
          })}

          {!leaderboard.meInTop && leaderboard.me && (
            <>
              <div style={{ textAlign: 'center', padding: '10px 0', color: 'var(--text-muted)', fontSize: 12 }}>•••</div>
              <div className="leader-row me">
                <span className="leader-rank">{toFa(leaderboard.me.rank)}</span>
                <span className="leader-avatar" style={{ '--av-color': 'var(--primary)' }}>
                  {user?.avatar?.startsWith('/') ? (
                    <img src={getImageUrl(user.avatar)} alt="" style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} />
                  ) : (
                    user?.avatar || '🦸'
                  )}
                </span>
                <span className="leader-name">شما</span>
                <span className="leader-score">🎯 {faNum(leaderboard.me.score)}٪</span>
              </div>
            </>
          )}

          <div style={{ marginTop: 14, textAlign: 'center' }}>
            <button className="sec-link" style={{ margin: '0 auto' }} onClick={() => navigate('/medals')}>
              📊 مشاهده جدول کامل
            </button>
          </div>
        </>
      )}
    </div>
  );

  const renderAchievements = () => (
    <div className="panel">
      <h3 className="panel-title">
        <span className="ico" style={{ '--icon-color': 'var(--emerald)' }}>🏅</span>
        دستاوردها
      </h3>
      <div className="achievement-grid">
        {achievements.list.map((ach, i) => (
          <div
            key={ach.id}
            className={`achievement ach-${(i % 6) + 1} ${ach.unlocked ? 'unlocked' : 'locked'}`}
            title={ach.label}
          >
            <span>{ach.emoji}</span>
            <span className="ach-label">{ach.label}</span>
          </div>
        ))}
      </div>
      {achievements.lockedCount > 0 && (
        <div style={{
          marginTop: 16, padding: '12px 14px',
          background: 'rgba(251, 191, 36, 0.08)',
          borderRadius: 14,
          border: '1px solid rgba(251, 191, 36, 0.2)',
          fontSize: 12.5, fontWeight: 700, color: 'var(--gold)',
          textAlign: 'center',
        }}>
          🎁 {toFa(achievements.lockedCount)} دستاورد دیگر تا باز شدن «افسانه»
        </div>
      )}
    </div>
  );

  const renderStoreTeaser = () => (
    <section className="store-teaser">
      <div className="store-inner">
        <div style={{ flexShrink: 0 }}>
          <div style={{
            width: 80, height: 80, borderRadius: 24,
            background: 'linear-gradient(135deg, var(--gold), var(--gold-dark))',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 40, boxShadow: '0 16px 40px -8px var(--gold)',
            animation: 'trophyFloat 3.5s ease-in-out infinite',
          }}>
            🛍️
          </div>
        </div>
        <div className="store-content">
          <h3 className="store-title">فروشگاه <span className="gold">سکه‌ها</span></h3>
          <p className="store-desc">
            سکه‌هات رو خرج کن: آزمون‌های طلایی، اسکین کاراکتر، راهنمایی هوش مصنوعی، و صندوق‌های شانس.
          </p>
        </div>
        <div className="store-actions">
          <button className="btn btn-gold" onClick={() => navigate('/coins')}>
            🪙 خرید سکه
          </button>
          <button className="btn btn-outline" onClick={() => navigate('/coins#box')}>
            🎁 صندوق شانس
          </button>
        </div>
      </div>
    </section>
  );

  const renderBottomNav = () => (
    <nav className="bottom-nav">
      <button className="nav-item" onClick={() => navigate('/StudentHome')}>
        <span className="ico">🏠</span>
        <span>خانه</span>
      </button>
      <button className="nav-item active">
        <span className="ico">📝</span>
        <span>آزمون‌ها</span>
      </button>
      <button className="nav-item" onClick={() => navigate('/student-lessons')}>
        <span className="ico">📚</span>
        <span>درس‌ها</span>
      </button>
      <button className="nav-item" onClick={() => navigate('/challenge')}>
        <span className="ico">🏆</span>
        <span>چالش‌ها</span>
      </button>
      <button className="nav-item" onClick={() => navigate('/avatars')}>
        <span className="ico">👤</span>
        <span>پروفایل</span>
      </button>
    </nav>
  );

  const renderScrollTop = () => (
    <button
      className={`scroll-top ${scrollVisible ? 'visible' : ''}`}
      onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
      aria-label="برو به بالا"
    >
      ↑
    </button>
  );

  // ═══════════════════════════════════════════════════════════════
  //  MODALS
  // ═══════════════════════════════════════════════════════════════

  const renderResultModal = () => {
    if (!resultExam) return null;
    const { exam, userResult } = resultExam;
    if (!userResult) return null;

    const maxScore = exam.questions?.reduce((s, q) => s + toNumber(q.score, 1), 0) || 0;
    const userScore = toNumber(userResult.score, 0);
    const percent = maxScore > 0 ? Math.round((userScore / maxScore) * 100) : 0;
    const passThreshold = calculatePassThreshold(maxScore);
    const isPassed = userScore >= passThreshold;
    const isFullyGraded = userResult.isEssayGraded !== false;
    const hasEssay = (exam.questions || []).some((q) => q.type === 'essay' || q.type === 'file');

    return (
      <div className="public-modal-overlay" onClick={() => setResultExam(null)}>
        <div className="public-modal-result" onClick={(e) => e.stopPropagation()}>
          <button className="public-modal-close" onClick={() => setResultExam(null)}>✕</button>

          <div style={{ textAlign: 'center', marginBottom: 20 }}>
            <div style={{ fontSize: 48, marginBottom: 8 }}>
              {userScore >= 18 ? '🌟' : userScore >= 16 ? '🎉' : userScore >= 12 ? '👍' : userScore >= 8 ? '💪' : '📖'}
            </div>
            <h2 style={{ fontSize: 22, fontWeight: 900, color: 'var(--text)', marginBottom: 4 }}>کارنامه آزمون</h2>
            <div style={{ fontSize: 14, color: 'var(--text-soft)', fontWeight: 600 }}>{exam.title}</div>
          </div>

          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            gap: 24, padding: '20px 0', marginBottom: 20,
            background: 'rgba(30, 38, 58, 0.4)', borderRadius: 20,
          }}>
            <div style={{
              width: 88, height: 88, borderRadius: '50%',
              background: 'linear-gradient(135deg, var(--primary), var(--secondary))',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: '0 12px 30px -8px var(--primary)',
            }}>
              <span style={{ fontSize: 34, fontWeight: 900, color: '#fff' }}>
                {hasEssay && !isFullyGraded ? '—' : faNum(userScore, 1)}
              </span>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 12, fontWeight: 800, color: 'var(--text-muted)', textTransform: 'uppercase' }}>نمره شما</div>
              <div style={{ fontSize: 22, fontWeight: 900, color: 'var(--text)' }}>از {faNum(maxScore)}</div>
              {!hasEssay || isFullyGraded ? (
                <div style={{ fontSize: 14, fontWeight: 800, color: isPassed ? 'var(--emerald)' : 'var(--rose)', marginTop: 4 }}>
                  {isPassed ? '✅ قبول' : '❌ مردود'} ({faNum(percent)}٪)
                </div>
              ) : (
                <div style={{ fontSize: 14, fontWeight: 800, color: 'var(--gold)', marginTop: 4 }}>⏳ در انتظار تصحیح</div>
              )}
            </div>
          </div>

          <div style={{ maxHeight: 300, overflowY: 'auto', marginBottom: 20 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: 'rgba(30, 38, 58, 0.6)' }}>
                  <th style={{ padding: '10px 8px', textAlign: 'center', fontWeight: 800, color: 'var(--text-muted)', fontSize: 11, textTransform: 'uppercase' }}>#</th>
                  <th style={{ padding: '10px 8px', textAlign: 'center', fontWeight: 800, color: 'var(--text-muted)', fontSize: 11, textTransform: 'uppercase' }}>نوع</th>
                  <th style={{ padding: '10px 8px', textAlign: 'center', fontWeight: 800, color: 'var(--text-muted)', fontSize: 11, textTransform: 'uppercase' }}>وضعیت</th>
                  <th style={{ padding: '10px 8px', textAlign: 'center', fontWeight: 800, color: 'var(--text-muted)', fontSize: 11, textTransform: 'uppercase' }}>نمره</th>
                </tr>
              </thead>
              <tbody>
                {(exam.questions || []).map((q, idx) => {
                  const isEssay = q.type === 'essay' || q.type === 'file';
                  const answer = userResult.answers?.find((a) => a.questionIndex === idx || a.questionId === q.id);
                  const detail = userResult.details?.find((d) => d.questionIndex === idx);
                  const isCorrect = detail?.isCorrect;
                  let status = '—';
                  let color = 'var(--text-muted)';
                  if (isEssay) {
                    if (isCorrect === true) { status = '✅ صحیح'; color = 'var(--emerald)'; }
                    else if (isCorrect === false) { status = '❌ غلط'; color = 'var(--rose)'; }
                    else { status = '⏳ انتظار'; color = 'var(--gold)'; }
                  } else {
                    if (answer?.isCorrect) { status = '✅ صحیح'; color = 'var(--emerald)'; }
                    else { status = '❌ غلط'; color = 'var(--rose)'; }
                  }
                  return (
                    <tr key={idx} style={{ borderBottom: '1px solid rgba(148, 163, 184, 0.15)' }}>
                      <td style={{ padding: '8px', textAlign: 'center', fontWeight: 800, color: 'var(--text-soft)' }}>{toFa(idx + 1)}</td>
                      <td style={{ padding: '8px', textAlign: 'center', fontSize: 12, color: 'var(--text-muted)' }}>{isEssay ? '📝 تشریحی' : '🎯 تستی'}</td>
                      <td style={{ padding: '8px', textAlign: 'center', fontWeight: 700, color, fontSize: 12 }}>{status}</td>
                      <td style={{ padding: '8px', textAlign: 'center', fontWeight: 800, color: 'var(--text-soft)' }}>
                        {isEssay && isCorrect == null ? '…' : faNum(detail?.score || 0, 1)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
            <button className="btn btn-primary" onClick={() => setResultExam(null)}>✕ بستن</button>
            <button className="btn btn-outline" onClick={() => window.print()}>🖨️ چاپ</button>
            {!hasEssay || isFullyGraded ? (
              exam.limitAttempts > 1 && resultExam.attemptsLeft > 0 && (
                <button className="btn btn-gold" onClick={() => {
                  setResultExam(null);
                  navigate(`/exams/take/${exam.id}`);
                }}>🔄 شرکت مجدد</button>
              )
            ) : null}
          </div>
        </div>
      </div>
    );
  };

  const renderCoinModal = () => {
    if (!coinModal) return null;
    const { exam, required, current } = coinModal;
    const shortage = Math.max(0, required - current);
    return (
      <div className="public-modal-overlay" onClick={() => setCoinModal(null)}>
        <div className="public-modal-coin" onClick={(e) => e.stopPropagation()}>
          <div style={{ fontSize: 56, textAlign: 'center', marginBottom: 12 }}>🪙</div>
          <h3 style={{ fontSize: 20, fontWeight: 900, color: 'var(--text)', textAlign: 'center', marginBottom: 8 }}>
            سکه کافی نیست
          </h3>
          <p style={{ fontSize: 14, color: 'var(--text-soft)', textAlign: 'center', marginBottom: 20, lineHeight: 1.9 }}>
            این آزمون <strong style={{ color: 'var(--gold)' }}>{faNum(required)} سکه</strong> هزینه دارد.<br />
            موجودی شما: <strong>{faNum(current)} سکه</strong><br />
            کمبود: <strong style={{ color: 'var(--rose)' }}>{faNum(shortage)} سکه</strong>
          </p>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', justifyContent: 'center' }}>
            <button className="btn btn-primary" onClick={() => { setCoinModal(null); navigate('/challenge'); }}>
              🎁 کسب سکه از چالش
            </button>
            <button className="btn btn-gold" onClick={goToCoinStore}>
              🪙 خرید سکه
            </button>
            <button className="btn btn-outline" onClick={() => setCoinModal(null)}>
              ✕ بستن
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderPreviewModal = () => {
    if (!previewExam) return null;
    const { exam, gradeTitle, fieldTitle } = previewExam;
    return (
      <div className="public-modal-overlay" onClick={() => setPreviewExam(null)}>
        <div className="public-modal-preview" onClick={(e) => e.stopPropagation()}>
          <button className="public-modal-close" onClick={() => setPreviewExam(null)}>✕</button>
          <div className="exam-header" style={{ marginBottom: 16 }}>
            <div className="exam-icon-wrap" style={{ width: 64, height: 64, fontSize: 32 }}>
              {getSubjectIcon(exam.subject)}
            </div>
          </div>
          <h2 style={{ fontSize: 20, fontWeight: 900, color: 'var(--text)', marginBottom: 12, lineHeight: 1.5 }}>
            {exam.title}
          </h2>
          {exam.description && (
            <p style={{ fontSize: 14, color: 'var(--text-soft)', lineHeight: 1.9, marginBottom: 16, padding: '12px 16px', background: 'rgba(30, 38, 58, 0.4)', borderRadius: 12 }}>
              {exam.description}
            </p>
          )}
          {exam.image && (
            <div style={{ marginBottom: 16, textAlign: 'center' }}>
              <img
                src={getImageUrl(exam.image)}
                alt="تصویر آزمون"
                style={{ maxWidth: '100%', maxHeight: 240, borderRadius: 16, border: '1.5px solid var(--border)' }}
              />
            </div>
          )}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 20 }}>
            {gradeTitle && <div style={{ padding: '10px 14px', background: 'rgba(30, 38, 58, 0.5)', borderRadius: 12, fontSize: 13, color: 'var(--text-soft)', fontWeight: 700 }}>🎓 {gradeTitle}</div>}
            {fieldTitle && <div style={{ padding: '10px 14px', background: 'rgba(30, 38, 58, 0.5)', borderRadius: 12, fontSize: 13, color: 'var(--text-soft)', fontWeight: 700 }}>🎯 {fieldTitle}</div>}
            {exam.subject && <div style={{ padding: '10px 14px', background: 'rgba(30, 38, 58, 0.5)', borderRadius: 12, fontSize: 13, color: 'var(--text-soft)', fontWeight: 700 }}>📖 {exam.subject}</div>}
            {exam.chapter && <div style={{ padding: '10px 14px', background: 'rgba(30, 38, 58, 0.5)', borderRadius: 12, fontSize: 13, color: 'var(--text-soft)', fontWeight: 700 }}>📑 {exam.chapter}</div>}
            <div style={{ padding: '10px 14px', background: 'rgba(30, 38, 58, 0.5)', borderRadius: 12, fontSize: 13, color: 'var(--text-soft)', fontWeight: 700 }}>📝 {faNum(exam.questions?.length || 0)} سوال</div>
            <div style={{ padding: '10px 14px', background: 'rgba(30, 38, 58, 0.5)', borderRadius: 12, fontSize: 13, color: 'var(--text-soft)', fontWeight: 700 }}>⏱ {faNum(exam.duration || 0)} دقیقه</div>
          </div>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
            <button className="btn btn-outline" onClick={() => setPreviewExam(null)}>✕ بستن</button>
            {previewExam.isFinalSubmit ? (
              <button className="btn btn-primary" onClick={() => { setPreviewExam(null); handleViewResult(previewExam); }}>
                🏆 کارنامه
              </button>
            ) : (
              <button className="btn btn-primary" onClick={() => { setPreviewExam(null); handleStartExam(previewExam); }}>
                {previewExam.btnText}
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  // ═══════════════════════════════════════════════════════════════
  //  MAIN RENDER
  // ═══════════════════════════════════════════════════════════════
  return (
    <div className={`public-exams-page ${isDark ? 'dark-mode' : 'light-mode'}`}>
      {/* Background */}
      <div className="bg-aurora">
        <div className="bg-blob blob-1" />
        <div className="bg-blob blob-2" />
        <div className="bg-blob blob-3" />
        <div className="bg-blob blob-4" />
      </div>
      <div className="bg-grid" />

      <div className="wrap">
        {renderTopBar()}
        {renderHero()}
        {renderTodayStats()}
        {renderDailyChallenge()}
        {renderFilters()}
        {renderExamGrid()}

        {/*<section className="league-panel">
          {renderLeague()}
          {renderAchievements()}
        </section>*/}

        {renderStoreTeaser()}
        {renderScrollTop()}
      </div>

      {/*{renderBottomNav()}*/}
      {renderResultModal()}
      {renderCoinModal()}
      {renderPreviewModal()}
    </div>
  );
};

export default PublicExamsPage;